import React, { useEffect, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { IconExclamationMark, IconCircleCheck } from "@tabler/icons";
import { ENUM_FORM_DISPLAY } from "../components/pageList";
import { useFormRender } from "../hooks/useFormRender";
import { useServerApi } from "../hooks/userServerApi";
import {
  getUserDetailFormSchema,
  PROFILE_TAB_KEYS,
} from "./operation/userDetailFormSchema";
import { Page } from "../layout/page";
import PageListHeader from "../components/pageListHeader";
import { useTabNavigate } from "../hooks/useTabNavigate";

const MyProfile = () => {
  const auth = useAuthUser();
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [pageTitle, setPageTitle] = useState(t("My Profile"));
  const [saving, setSaving] = useState(false);

  const formSchema = getUserDetailFormSchema(null, null, {
    title: "My Profile",
    displayMode: ENUM_FORM_DISPLAY.INSIDE,
    showSaveBar: false,
    tabKeys: PROFILE_TAB_KEYS,
    includeActionList: false,
    includeOverriding: false,
    readOnly: true,
    editableProviderLogin: true,
    showSfcLicencesInCpd: false,
    consultantViewFiles: true,
    hideInternalFiles: true,
  });

  const [renderForm, formAction, formStatus] = useFormRender(
    formSchema,
    null,
    null,
    (values) => {
      if (values?.name) {
        setPageTitle(`${t("My Profile")} > ${values.name}`);
      }
    }
  );

  useEffect(() => {
    formAction.open({ mode: "show", id: auth().id });
  }, []);

  const handleSaveProviderLogin = async () => {
    setSaving(true);
    try {
      const values = formAction.getValues();
      const result = await api.updateById({
        apiEntity: "user",
        id: auth().id,
        values: { providerLogin: values.providerLogin ?? [] },
      });

      if (!result?.success) {
        throw new Error(api.getErrorMsg(result?.error));
      }

      showNotification({
        title: t("Save Success"),
        message: t("Provider login updated"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
      });
    } catch (error) {
      showNotification({
        title: t("Save Fail"),
        message: error?.message ?? String(error),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={pageTitle}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSaveProviderLogin,
            loading: saving,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {renderForm()}
    </Page>
  );
};

export default MyProfile;
